import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Phone, Mail, ShieldCheck, MessageSquare, Calendar, Tag } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import QuoteDialog, { partImageUrl } from "@/components/QuoteDialog";

const fmtR = (n) =>
  new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n || 0);

export default function PartDetailPage() {
  const { slug } = useParams();
  const [part, setPart] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [busy, setBusy] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    axios.get(`${API}/parts/by-slug/${slug}`)
      .then((r) => {
        if (!alive) return;
        const { supplier: sup, ...p } = r.data;
        setPart(p);
        setSupplier(sup);
      })
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setBusy(false));
    return () => { alive = false; };
  }, [slug]);

  if (busy) {
    return <div className="max-w-3xl mx-auto px-6 py-16 font-mono-tech text-sm text-neutral-500">[ Loading part... ]</div>;
  }
  if (notFound || !part) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="part-not-found">
        <Helmet>
          <title>Part not found · Spannr</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="text-xs font-mono-tech tracking-[0.25em] text-[#FF5100] mb-2">404 · NOT FOUND</div>
        <h1 className="font-display text-3xl font-extrabold mb-3">This part is no longer listed.</h1>
        <p className="text-sm text-neutral-600 mb-6">
          It may have sold or been delisted by the supplier.
        </p>
        <Link to="/" className="px-5 h-11 inline-flex items-center bg-[#FF5100] hover:bg-[#E64A00] text-white text-xs font-bold uppercase tracking-wider">
          Search again
        </Link>
      </div>
    );
  }

  const titleText = `${part.year_from || ""}${part.year_to && part.year_to !== part.year_from ? "-" + part.year_to : ""} ${part.make} ${part.model} ${part.name} — ${fmtR(part.price_zar)} | Spannr`.trim();
  const desc = `${part.condition === "new" ? "New" : part.condition === "used" ? "Used" : "Refurbished"} ${part.name} for ${part.year_from} ${part.make} ${part.model}${part.engine ? " " + part.engine : ""}. ${fmtR(part.price_zar)} from ${part.supplier_name} in ${part.supplier_city}, South Africa. ${part.warranty_months ? `${part.warranty_months}-month warranty.` : ""} Get a quote on Spannr — SA's AI parts network.`.replace(/\s+/g, " ").trim();
  const url = `https://spannr.co.za/parts/${slug}`;
  const imgPaths = part.image_paths || part.images || [];
  const imgUrl = imgPaths[0] ? partImageUrl(imgPaths[0]) : "https://spannr.co.za/og-image.png";

  // Schema.org Product structured data — supercharges Google rich results
  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": `${part.year_from} ${part.make} ${part.model} ${part.name}`,
    "description": desc,
    "image": imgUrl,
    "sku": part.oem_number || part.id,
    "brand": { "@type": "Brand", "name": part.make },
    "offers": {
      "@type": "Offer",
      "url": url,
      "priceCurrency": "ZAR",
      "price": part.price_zar,
      "availability": part.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "itemCondition":
        part.condition === "new" ? "https://schema.org/NewCondition" :
        part.condition === "used" ? "https://schema.org/UsedCondition" :
        "https://schema.org/RefurbishedCondition",
      "seller": {
        "@type": "Organization",
        "name": part.supplier_name,
        "address": { "@type": "PostalAddress", "addressLocality": part.supplier_city, "addressCountry": "ZA" },
      },
    },
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="part-detail">
      <Helmet>
        <title>{titleText}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={titleText} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={imgUrl} />
        <meta property="product:price:amount" content={String(part.price_zar)} />
        <meta property="product:price:currency" content="ZAR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={titleText} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content={imgUrl} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      <Link to="/browse" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-mono-tech text-neutral-500 hover:text-[#FF5100] mb-4">
        <ArrowLeft size={12} /> Back to browse
      </Link>

      <div className="grid lg:grid-cols-[1.2fr,1fr] gap-6 lg:gap-10">
        {/* Image */}
        <div>
          <div className="aspect-[4/3] bg-neutral-100 border border-neutral-200 grid place-items-center overflow-hidden">
            {imgPaths.length > 0 ? (
              <img src={partImageUrl(imgPaths[0])} alt={`${part.make} ${part.model} ${part.name}`} className="w-full h-full object-cover" />
            ) : (
              <div className="text-xs font-mono-tech text-neutral-400 tracking-wider">[ NO IMAGE ]</div>
            )}
          </div>
          {imgPaths.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              {imgPaths.slice(1, 5).map((src, i) => (
                <img key={i} src={partImageUrl(src)} alt="" className="aspect-square object-cover border border-neutral-200" />
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-mono-tech text-[#FF5100]">
            {part.year_from}{part.year_to && part.year_to !== part.year_from ? `–${part.year_to}` : ""} · {part.make} {part.model}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold leading-[1.05] tracking-tight mt-1">
            {part.name}
          </h1>

          <div className="mt-4 flex items-baseline gap-2">
            <div className="font-display text-3xl font-extrabold">{fmtR(part.price_zar)}</div>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border border-neutral-200">
              {part.condition || "—"}
            </span>
            {part.in_stock && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">
                In stock
              </span>
            )}
          </div>

          <dl className="mt-6 space-y-2 text-sm">
            <Row label="Engine" value={part.engine} icon={Tag} />
            <Row label="OEM number" value={part.oem_number} icon={Tag} />
            <Row label="Warranty" value={part.warranty_months ? `${part.warranty_months} months` : null} icon={Calendar} />
            <Row label="Supplier" value={part.supplier_name} icon={ShieldCheck} />
            <Row label="Location" value={part.supplier_city} icon={MapPin} />
          </dl>

          {part.description && (
            <div className="mt-5 pt-5 border-t border-neutral-100">
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1">Description</div>
              <p className="text-sm leading-relaxed text-neutral-700 whitespace-pre-line">{part.description}</p>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              data-testid="part-detail-quote"
              onClick={() => setQuoteOpen(true)}
              className="px-5 h-11 bg-[#FF5100] hover:bg-[#E64A00] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
            >
              <MessageSquare size={12} /> Request quote
            </button>
            {supplier?.phone && (
              <a
                href={`tel:${supplier.phone}`}
                className="px-5 h-11 border border-neutral-300 hover:border-neutral-900 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
              >
                <Phone size={12} /> {supplier.phone}
              </a>
            )}
            {supplier?.email && (
              <a
                href={`mailto:${supplier.email}?subject=${encodeURIComponent(`Quote: ${part.name} for ${part.year_from} ${part.make} ${part.model}`)}`}
                className="px-5 h-11 border border-neutral-300 hover:border-neutral-900 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
              >
                <Mail size={12} /> Email
              </a>
            )}
          </div>
        </div>
      </div>

      {quoteOpen && <QuoteDialog part={part} onClose={() => setQuoteOpen(false)} />}
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-neutral-100">
      <Icon size={14} className="text-neutral-400 shrink-0" />
      <span className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 w-24 shrink-0">{label}</span>
      <span className="text-sm font-medium truncate">{value}</span>
    </div>
  );
}
